/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { OutlinedInput, OutlinedInputProps } from '@mui/material';
import { FC } from 'react';

import { uuid } from '../../../util/uuid';
import FormControlMessage from '../FormControlMessage/FormControlMessage';
import FormControlLabel, { TLabelColor } from '../FormControlLabel/FormControlLabel';
import TestIds from '../../../testIds';
import { StyledFormControl } from '../StyledFormControl';

export interface IInputFormControlProps extends OutlinedInputProps {
  'data-testid'?: string;
  hint?: string;
  errorMessage?: string;
  maxWidth?: string | number;
  labelColor?: TLabelColor;
}

const InputFormControl: FC<IInputFormControlProps> = ({
  id = uuid(),
  label,
  hint,
  error,
  errorMessage,
  required,
  disabled,
  maxWidth,
  labelColor = 'primary',
  'data-testid': testId,
  inputProps = {},
  ...outlinedInputProps
}: IInputFormControlProps): JSX.Element => (
  <StyledFormControl
    data-testid={TestIds.components.formControls.inputFormControl.component(testId)}
    className=''
    maxWidth={maxWidth}
  >
    {label && (
      <FormControlLabel
        data-testid={TestIds.components.formControls.inputFormControl.label}
        htmlFor={id}
        required={required}
        color={labelColor}
      >
        {label}
      </FormControlLabel>
    )}
    <OutlinedInput
      inputProps={{
        'data-testid': TestIds.components.formControls.inputFormControl.input,
        id,
        maxLength: 200,
        ...inputProps,
      }}
      error={error}
      required={required}
      disabled={disabled}
      {...outlinedInputProps}
    />
    <FormControlMessage
      data-testid={TestIds.components.formControls.inputFormControl.message}
      error={error}
      errorMessage={
        disabled && error ? 'Invalid input. Please contact Farmlands to update.' : errorMessage
      }
      hint={hint}
    ></FormControlMessage>
  </StyledFormControl>
);

export default InputFormControl;
